/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import {
    animate,
    AnimationTriggerMetadata,
    state,
    style,
    transition,
    trigger
} from '@angular/animations';

/** Time and timing curve for expansion panel animations. */
export const EXPANSION_PANEL_ANIMATION_TIMING = '225ms cubic-bezier(0.4,0.0,0.2,1)';

/**
 * Animations used by the Material expansion panel.
 *
 * A bug in angular animation's `state` when ViewContainers are moved using ViewContainerRef.move()
 * causes the animation state of moved components to become `void` upon exit, and not update again
 * upon reentry into the DOM.  This can lead a to situation for the expansion panel where the state
 * of the panel is `expanded` or `collapsed` but the animation state is `void`.
 *
 * To correctly handle animating to the next state, we animate between `void` and `collapsed` which
 * are defined to have the same styles. Since angular animates from the current styles to the
 * destination state's style definition, in situations where we are moving from `void`'s styles to
 * `collapsed` this acts a noop since no style values change.
 *
 * In the case where angular's animation state is out of sync with the expansion panel's state, the
 * expansion panel being `expanded` and angular animations being `void`, the animation from the
 * `expanded`'s effective styles (though in a `void` animation state) to the collapsed state will
 * occur as expected.
 *
 * Angular Bug: https://github.com/angular/angular/issues/18847
 *
 * @docs-private
 */
export const matExpansionAnimations: {
    readonly indicatorRotate: AnimationTriggerMetadata;
    readonly bodyExpansion: AnimationTriggerMetadata;
    readonly zoomInFilter: AnimationTriggerMetadata;
    readonly zoomInInfo: AnimationTriggerMetadata;
    readonly zoomInRange: AnimationTriggerMetadata;
    readonly zoomInDownload: AnimationTriggerMetadata;
    readonly growShrink: AnimationTriggerMetadata;
} = {
    /** Animation that rotates the indicator arrow. */
    indicatorRotate: trigger('indicatorRotate', [
        state('collapsed, void', style({ transform: 'rotate(0deg)' })),
        state('expanded', style({ transform: 'rotate(180deg)' })),
        transition('expanded <=> collapsed, void => collapsed',
        animate(EXPANSION_PANEL_ANIMATION_TIMING))
    ]),

    /** Animation that expands and collapses the panel content. */
    bodyExpansion: trigger('bodyExpansion', [
        state('collapsed, void', style({ height: '0px', visibility: 'hidden' })),
        state('expanded', style({ height: '*', visibility: 'visible' })),
        transition('expanded <=> collapsed, void => collapsed',
        animate(EXPANSION_PANEL_ANIMATION_TIMING))
    ]),

    zoomInFilter: trigger('zoomInFilter', [
        state('collapsed', style({ left: '120px', visibility: 'hidden' })),
        state('expanded', style({ left: 0, visibility: 'visible' })),
        transition('expanded <=> collapsed',
        animate(EXPANSION_PANEL_ANIMATION_TIMING))
    ]),

    zoomInInfo: trigger('zoomInInfo', [
        state('collapsed', style({ left: '80px', visibility: 'hidden' })),
        state('expanded', style({ left: 0, visibility: 'visible' })),
        transition('expanded <=> collapsed',
        animate(EXPANSION_PANEL_ANIMATION_TIMING))
    ]),

    zoomInRange: trigger('zoomInRange', [
        state('collapsed', style({ left: '370px', visibility: 'hidden' })),
        state('expanded', style({ left: 0, visibility: 'visible' })),
        transition('expanded <=> collapsed',
        animate(EXPANSION_PANEL_ANIMATION_TIMING))
    ]),

    zoomInDownload: trigger('zoomInDownload', [
        state('collapsed', style({ left: '40px', visibility: 'hidden' })),
        state('expanded', style({ left: 0, visibility: 'visible' })),
        transition('expanded <=> collapsed',
        animate(EXPANSION_PANEL_ANIMATION_TIMING))
    ]),

    growShrink: trigger('growShrink', [
        state('collapsed, void', style({ visibility: 'hidden', transform: 'scale(0)' })),
        state('expanded', style({ visibility: 'visible', transform: 'scale(1)' })),
        transition('expanded <=> collapsed',
        animate(EXPANSION_PANEL_ANIMATION_TIMING))
    ])
};
